import { Drawer, ProductLockup } from '@fruuit/ui'
import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router'
import type { AreaId } from '../api/types'
import { AREAS } from '../areas/areas'
import { useSession } from '../auth/session-context'
import { AreaSwitcher } from './AreaSwitcher'
import { ImpersonationBanner, SampleBanner, ShellFooter } from './Banners'
import { HelpNavLink } from './HelpLink'
import { rememberArea } from './lastArea'
import { SideNav } from './SideNav'
import { TopBar } from './TopBar'

/**
 * Authenticated layout: banners, sticky top bar, area side nav (a drawer below 1024 px),
 * the page outlet in `<main>` and the footer disclaimer.
 */
export function AppShell({ area }: { area: AreaId }) {
  const me = useSession()
  const { pathname } = useLocation()
  const mainRef = useRef<HTMLElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [navOpen, setNavOpen] = useState(false)
  const firstRender = useRef(true)
  const [focusRequest, setFocusRequest] = useState(0)
  const focusPending = useRef(false)

  useEffect(() => {
    rememberArea(area)
  }, [area])

  // NAV-008 / UI-051: a route change closes the mobile nav and moves focus to the new content.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger, not an input
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    setNavOpen(false)
    window.scrollTo({ top: 0 })
    scrollRef.current?.scrollTo?.({ top: 0 })
    focusPending.current = true
    setFocusRequest((count) => count + 1)
  }, [pathname])

  // Focus moves only once the drawer is gone: while it is open, <main> is inert.
  // biome-ignore lint/correctness/useExhaustiveDependencies: focusRequest is the trigger
  useEffect(() => {
    if (!focusPending.current || navOpen) return
    focusPending.current = false
    mainRef.current?.focus({ preventScroll: true })
  }, [focusRequest, navOpen])

  return (
    // From 1024 px the shell is a fixed frame: header on top, nav and content scroll on their own.
    <div className="flex min-h-dvh flex-col bg-cream lg:h-dvh lg:overflow-hidden print:h-auto print:overflow-visible">
      <a
        href="#main"
        className="sr-only z-[80] rounded-md bg-teal px-4 py-2 text-sm font-semibold text-white focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Skip to content
      </a>
      <ImpersonationBanner me={me} />
      <SampleBanner area={area} />
      <TopBar area={area} onOpenNav={() => setNavOpen(true)} />

      <div className="flex flex-1 lg:min-h-0">
        <aside
          aria-label="Sidebar"
          className="hidden w-sidebar shrink-0 flex-col overflow-y-auto border-r border-line bg-wash lg:flex print:hidden"
        >
          <SideNav area={area} className="px-3 pt-7" />
          <div className="mt-auto flex flex-col gap-5 px-3 pt-8 pb-6">
            <HelpNavLink />
            <p className="px-3 text-xs leading-relaxed text-sage">
              Fruuit Consulting
              <br />
              Research-led stewardship
            </p>
          </div>
        </aside>

        <div
          ref={scrollRef}
          className="flex min-w-0 flex-1 flex-col lg:overflow-y-auto print:overflow-visible"
        >
          <main
            id="main"
            ref={mainRef}
            tabIndex={-1}
            className="mx-auto w-full max-w-content flex-1 px-4 py-7 focus:outline-none sm:px-8 lg:px-10 lg:py-10"
          >
            <Outlet />
          </main>
          <ShellFooter scope={AREAS[area].scope} />
        </div>
      </div>

      <Drawer
        open={navOpen}
        onClose={() => setNavOpen(false)}
        title="Navigation"
        hideTitle
        side="left"
      >
        <div className="flex min-h-full flex-col gap-6 px-3 pb-6">
          <div className="px-3">
            <ProductLockup />
          </div>
          <AreaSwitcher current={area} orientation="vertical" className="px-3" />
          <SideNav area={area} />
          <HelpNavLink className="mt-auto border-t border-line pt-4" />
        </div>
      </Drawer>
    </div>
  )
}
